import { fetchJson, mutate, qs } from './base-roros.service';

// ─── Cadence ────────────────────────────────────────────────────────────────

/**
 * M-Rhythm-1: structured schedule_config shape per cadence type.
 * Weekday strings match the backend next-occurrence helper convention:
 * 'mon'|'tue'|'wed'|'thu'|'fri'|'sat'|'sun'
 */
export interface CadenceScheduleConfig {
  time: string; // HH:MM
  // daily
  weekdays?: string[];
  // weekly / monthly / annual / quarterly / trimester
  weekday?: string;
  // monthly / annual
  ordinal?: string;
  // annual
  month?: number;
  // quarterly / trimester
  offset_weeks?: number;
  offset_direction?: 'before' | 'after';
  offset_anchor?: 'start' | 'end';
  offset_period?: 'upcoming' | 'current';
}

/** Wave-3 C8: stored participant shape (matches StoredDefaultParticipant on the server). */
export interface StoredParticipant {
  participantKind: 'actor' | 'domain_accountable';
  actorId: string | null;
  domainId: string | null;
  role: 'attendee' | 'observer';
}

// CB-006: Intentional parallel definition — server/modules/cadence/cadence.types.ts is source of truth.
// Keep these in sync when updating the DTO shape.
/** M-Rhythm-1: agenda item on a cadence schedule template. */
export interface CadenceAgendaItemDTO {
  id: string;
  cadenceScheduleId: string;
  sortOrder: number;
  title: string;
  accountableActorId: string | null;
  accountableDomainId: string | null;
  durationMinutes: number | null;
}

export interface RorosCadenceScheduleDTO {
  id: string;
  orgId: string;
  cadenceType: string;
  /** Cron expression — matches backend `schedule_expression` column. */
  scheduleExpression: string;
  /** M-Rhythm-1: structured schedule config (replaces cron for display/logic). */
  scheduleConfig: CadenceScheduleConfig | null;
  /** M-Rhythm-1: default accountable actor id for materialized meetings. */
  defaultAccountableActorId: string | null;
  /** M-Rhythm-1: default moderator actor id for materialized meetings. */
  defaultModeratorActorId: string | null;
  isActive: boolean;
  operatingModeMinimum: string | null;
  contentTemplate: Record<string, unknown>;
  /** W8-1: pre-computed count of agenda template items (avoids lazy-load flash). */
  agendaItemCount: number;
  /** W8-1: pre-computed sum of agenda item durations in minutes. */
  agendaTotalDuration: number;
  createdAt: string;
  updatedAt: string;
}

export interface RorosCadenceReviewDTO {
  id: string;
  orgId: string;
  cadenceType: string;
  domainId: string | null;
  status: string;
  agenda: Record<string, unknown> | null;
  summary: string | null;
  actionItems: Record<string, unknown>[] | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const RorosCadenceService = {
  listSchedules: () =>
    fetchJson<RorosCadenceScheduleDTO[]>('/api/v1/cadence/schedules'),
  updateSchedule: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosCadenceScheduleDTO>(`/api/v1/cadence/schedules/${id}`, 'PUT', payload),
  /** M-Rhythm-1: update the structured schedule_config for a cadence row. */
  updateScheduleConfig: (id: string, scheduleConfig: CadenceScheduleConfig) =>
    mutate<RorosCadenceScheduleDTO>(`/api/v1/cadence/schedules/${id}/config`, 'PUT', scheduleConfig),
  /**
   * M-Rhythm-1: set default accountable + moderator actors for materialized meetings.
   * Wave-3 C8: also accepts `default_participants` array (optional).
   */
  setDefaults: (id: string, payload: {
    default_accountable_actor_id: string | null;
    default_moderator_actor_id: string | null;
    default_participants?: StoredParticipant[];
  }) =>
    mutate<RorosCadenceScheduleDTO>(`/api/v1/cadence/schedules/${id}/defaults`, 'PUT', payload),
  /** M-Rhythm-1: list template agenda items for a cadence schedule. */
  listAgendaTemplate: (id: string) =>
    fetchJson<CadenceAgendaItemDTO[]>(`/api/v1/cadence/schedules/${id}/agenda-template`),
  /** M-Rhythm-1: replace the full agenda template atomically. */
  setAgendaTemplate: (id: string, items: Array<{ title: string; accountable_actor_id?: string | null; accountable_domain_id?: string | null; duration_minutes?: number | null; sort_order?: number }>) =>
    mutate<CadenceAgendaItemDTO[]>(`/api/v1/cadence/schedules/${id}/agenda-template`, 'PUT', { items }),
  createReview: (payload: Record<string, unknown>) =>
    mutate<RorosCadenceReviewDTO>('/api/v1/cadence/reviews', 'POST', payload),
  listReviews: (params?: Record<string, string>) =>
    fetchJson<RorosCadenceReviewDTO[]>(`/api/v1/cadence/reviews${qs(params)}`),
  getReview: (id: string) =>
    fetchJson<RorosCadenceReviewDTO>(`/api/v1/cadence/reviews/${id}`),
  updateReview: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosCadenceReviewDTO>(`/api/v1/cadence/reviews/${id}`, 'PUT', payload),
  completeReview: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosCadenceReviewDTO>(`/api/v1/cadence/reviews/${id}/complete`, 'POST', payload),
};
